import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { getCookie } from '@utils/cookies';
import { normaliseShopifyPrice } from '@utils/prices';
import { consolidateFrames } from '@utils/product';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDelegateCash } from '../components/Web3/Web3Provider/useDelegateCash';
import { useWeb3Cache } from '../components/Web3/Web3Provider/useWeb3Cache';
import {
  FF_FRAMING_CONTENT_RELEASE_PAGE,
  FF_FRAMING_PREVIEWER_PRODUCT_PAGE,
  FF_FRAMING_PREVIEWER_RELEASE_PAGE,
  FF_PRODUCT_STICKY_CTA_BOTTOM,
  FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON,
} from '../constants/featureFlags';
import { DEFAULT_CURRENCY_CODE } from '../constants/locations';
import { AML_LIMIT } from '../constants/product';
import {
  parseFrameVariantIdQuery,
  resolveFrameQuery,
} from '../features/checkout/utils/framingQuery';
import { useAuth } from '../providers';
import {
  AuthErrorResponse,
  AuthGateway,
  GenerateMultipassTokenResponse,
} from '../types/Auth';
import { ProductGateway } from '../types/AvantArteProduct';
import {
  GeneratedTaxType,
  useCalculateOrderPricingLazyQuery,
} from '../types/generated';
import { GiftCardCheckoutPayload } from '../types/GiftCard';
import { CurrencyCode } from '../types/Locations';
import { Frame, FrameOption, FrameOptionConsolidated } from '../types/Products';
import type {
  AttributeInput,
  CheckoutGateway,
  CollectionSetProduct,
  ProductPurchaseDetails,
  ProductPurchasingController,
  ProductsGateway,
  ProductUnlockGateway,
  UnlockMessage,
} from '../types/Purchasing';
import { TrackUnlockCode } from '../utils/analytics';
import { getDispatchDate } from '../utils/dispatch';
import { framedByDefault } from '../utils/frames';
import { shopifyGraphqlIdToAdminId } from '../utils/shopifyIds';
import { useCountryCode } from './useCountryCode';
import { useCurrency } from './useCurrency';
import { useOnBfCacheLoad } from './useOnBfCacheLoad';
import { ReleaseGatingController } from './useReleaseGating';

export type Options<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
> = {
  identifier: TProductIdentifier;
  release?: string;
  productsGateway: ProductsGateway<TProductIdentifier, TProduct>;
  checkoutGateway: CheckoutGateway<TProduct, GiftCardCheckoutPayload>;
  authGateway?: AuthGateway;
  unlockGateway: ProductUnlockGateway<TProductIdentifier>;
  releaseGating?: ReleaseGatingController;
  releaseType?: StoryblokReleaseType;
  aaProductsGateway: ProductGateway;
  variantIdentifier?: string;
  isPrivateSale?: boolean;
  privateLinkIdentifier?: string;
  preview?: boolean;
  productDetails: TProduct | null;
};

export function useProductPurchasing<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
>(
  options: Options<TProductIdentifier, TProduct>,
): ProductPurchasingController<TProduct> {
  const [currency] = useCurrency();
  const [countryCode] = useCountryCode();
  const [amlCheckNeeded, setAmlCheckNeeded] = useState(false);
  const [product, setProduct] = useState<TProduct | null>(
    options.productDetails || null,
  );
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [frame, setFrame] = useState<Frame | null>(null);
  const [frameOptions, setFrameOptions] = useState<Array<FrameOption> | null>(
    null,
  );
  const [isPreSelectedCollectionSet, setIsPreSelectedCollectionSet] =
    useState(false);
  const isFramedByDefaultFeatureEnabled = useFeatureIsOn('framed-by-default');

  const experimentalProductTileSubscriptionButton = useFeatureIsOn(
    FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON,
  );
  const framingContentReleasePage = useFeatureIsOn(
    FF_FRAMING_CONTENT_RELEASE_PAGE,
  );
  const framingPreviewerProductPage = useFeatureIsOn(
    FF_FRAMING_PREVIEWER_PRODUCT_PAGE,
  );
  const framingPreviewerReleasePage = useFeatureIsOn(
    FF_FRAMING_PREVIEWER_RELEASE_PAGE,
  );

  const experimentalStickyCtaBottom = useFeatureIsOn(
    FF_PRODUCT_STICKY_CTA_BOTTOM,
  );

  const [frameOptionsConsolidated, setFrameOptionsConsolidated] =
    useState<Array<FrameOptionConsolidated> | null>(null);

  const [frameRecommendedTitle, setFrameRecommendedTitle] = useState<
    string | null
  >(null);
  const [frameRecommendedLabel, setFrameRecommendedLabel] = useState<
    string | null
  >(null);

  // A frame chosen elsewhere (e.g. the release page previewer) and linked
  // as `?frame=<variant>` or `?frame=none`; applied once, on first load.
  const router = useRouter();
  const linkedFrameId = parseFrameVariantIdQuery(router.query);
  const linkedFrameApplied = useRef(false);
  // The frame the link applied, so checkout can carry it on while it's kept.
  const [linkedFrame, setLinkedFrame] = useState<Frame | null | undefined>(
    undefined,
  );

  // The page's own selection and frame from before "select all" switched to
  // the whole collection set, and the frame to put back once it's restored.
  const [beforeCollectionSetToggleAll, setBeforeCollectionSetToggleAll] =
    useState<{
      products: CollectionSetProduct[];
      frame: Frame | null;
    } | null>(null);
  const restoredFrame = useRef<{ frame: Frame | null } | null>(null);

  useEffect(() => {
    // Waits for the restored selection's options, so the default frame
    // below doesn't replace the one the page had.
    if (restoredFrame.current) {
      setFrame(restoredFrame.current.frame);
      restoredFrame.current = null;
      return;
    }
    if (!frameOptionsConsolidated?.length) return;

    if (!linkedFrameApplied.current) {
      const linked = resolveFrameQuery(frameOptionsConsolidated, linkedFrameId);

      if (linked !== undefined) {
        linkedFrameApplied.current = true;
        setLinkedFrame(linked);
        setFrame(linked);
        return;
      }
    }
    if (linkedFrameApplied.current) return;

    const shouldPreselectFrame =
      isFramedByDefaultFeatureEnabled ||
      framedByDefault(product?.framingOffered);

    if (shouldPreselectFrame) {
      const { variants, ...frameRest } = frameOptionsConsolidated[0];
      const selectedFrame = {
        ...frameRest,
        variant: {
          title: variants[0].title,
          consolidatedVariants: variants[0].consolidatedVariants,
        },
      };

      setFrame(selectedFrame);
    }
  }, [
    frameOptionsConsolidated,
    isFramedByDefaultFeatureEnabled,
    linkedFrameId,
    product?.framingOffered,
  ]);

  const [unlocked, setUnlocked] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [requiresUnlock, setRequiresUnlocking] = useState(false);
  const [unlockCode, setUnlockCode] = useState<string | undefined>(undefined);
  const [unlockMessage, setUnlockMessage] = useState<UnlockMessage | undefined>(
    undefined,
  );
  const [quantity, setProductQuantity] = useState(1);
  const [collectionSetProducts, setCollectionSetProducts] = useState<
    Array<CollectionSetProduct>
  >([]);

  const [collectionSetDescription, setCollectionSetDescription] = useState<
    string | null
  >(null);
  const [collectionSetImage, setCollectionSetImage] = useState<string | null>(
    null,
  );

  const { isLoggedIn } = useAuth();

  const items = useMemo(() => {
    if (!product) return [];

    const selectedItems = [];

    if (collectionSetProducts.length > 0) {
      selectedItems.push(
        ...collectionSetProducts
          .filter(({ selected }) => selected)
          .map((release) => ({
            variantId: release.shopifyVariantId,
            quantity: release.quantity,
          })),
      );
    } else {
      selectedItems.push({
        variantId: product.shopifyVariantId!,
        quantity,
      });
    }

    if (frame) {
      frame.variant.consolidatedVariants.forEach((variant) => {
        selectedItems.push({
          variantId: variant.shopifyFrameVariantId,
          quantity: variant.quantity,
        });
      });
    }

    return selectedItems;
  }, [product, collectionSetProducts, frame, quantity]);

  const [calculateOrderPricing] = useCalculateOrderPricingLazyQuery();

  const requiresGating =
    !!options.releaseGating && !!options.releaseGating.requiresNftGating;

  // The latest fetch, so a slower, earlier one can't overwrite it.
  const latestRequest = useRef(0);
  // Starts at the first product, so its first load keeps whatever the caller
  // set up on mount (a private room's unlock code) rather than resetting it.
  const shownIdentifier = useRef<TProductIdentifier>(options.identifier);

  const { walletAddress } = useWeb3Cache();
  const delegateCash = useDelegateCash();

  const canPurchase =
    !requiresGating ||
    (!!options.releaseGating && !!options.releaseGating.passedGating);

  // whenever the quantity of a single product, or one of the products in
  // the collection set changes, we need to recalculate the frame options
  useEffect(() => {
    if (!frameOptions) {
      return;
    }

    // collection sets only
    if (collectionSetProducts.length) {
      const selection = collectionSetProducts
        .filter(({ selected }) => selected)
        .map((selectedProduct) => ({
          id: selectedProduct.shopifyId,
          quantity: selectedProduct.quantity,
        }));
      const consolidated = consolidateFrames({
        frameOptions,
        selection,
        compositeImageProductShopifyId: product?.shopifyId ?? '',
        frameRecommendedLabel,
        frameRecommendedTitle,
      });

      setFrameOptionsConsolidated(consolidated);

      return;
    }
    // regular purchase of a single product
    const consolidated = consolidateFrames({
      frameOptions,
      selection: [
        {
          id: product?.shopifyId ?? '',
          quantity,
        },
      ],
      compositeImageProductShopifyId: product?.shopifyId ?? '',
      frameRecommendedLabel,
      frameRecommendedTitle,
    });

    setFrameOptionsConsolidated(consolidated);
  }, [quantity, collectionSetProducts, frameOptions, product?.shopifyId]);

  // The product on its own, for framing it outside a collection set selection.
  const productFrameOptions = useMemo(
    () =>
      frameOptions
        ? consolidateFrames({
            frameOptions,
            selection: [{ id: product?.shopifyId ?? '', quantity: 1 }],
            compositeImageProductShopifyId: product?.shopifyId ?? '',
            frameRecommendedLabel,
            frameRecommendedTitle,
          })
        : null,
    [
      frameOptions,
      frameRecommendedLabel,
      frameRecommendedTitle,
      product?.shopifyId,
    ],
  );

  const resetPurchasingState = useCallback(() => {
    // A new product takes its own linked or default frame.
    linkedFrameApplied.current = false;
    setLinkedFrame(undefined);
    setBeforeCollectionSetToggleAll(null);
    restoredFrame.current = null;
    setFrame(null);
    setFrameOptions(null);
    setFrameOptionsConsolidated(null);
    setFrameRecommendedTitle(null);
    setFrameRecommendedLabel(null);
    setCollectionSetProducts([]);
    setCollectionSetDescription(null);
    setCollectionSetImage(null);
    setUnlocked(false);
    setUnlockLoading(false);
    setRequiresUnlocking(false);
    setUnlockCode(undefined);
    setUnlockMessage(undefined);
    setProductQuantity(1);
  }, []);

  const updatePurchaseInfo = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);

    const purchasingDetails =
      await options.productsGateway.getProductPurchaseDetails({
        identifier: options.identifier,
        releaseName: options.release || null,
        variantIdentifier: options.variantIdentifier,
      });

    if (request !== latestRequest.current) return;

    // Reset only once the next product has arrived, in the same render as its
    // details, so switching artwork option doesn't collapse the framing and
    // set rows (and the gallery's "View as set") while it loads.
    if (shownIdentifier.current !== options.identifier) {
      resetPurchasingState();
      shownIdentifier.current = options.identifier;
    }

    if (purchasingDetails) {
      setProduct(purchasingDetails);

      if (purchasingDetails.requiresUnlockCode) {
        setRequiresUnlocking(purchasingDetails.requiresUnlockCode);
      }

      setFrameOptions(purchasingDetails.frames);
      setFrameRecommendedTitle(purchasingDetails.frameRecommendedTitle);
      setFrameRecommendedLabel(purchasingDetails.frameRecommendedLabel);

      if (purchasingDetails.collectionSet) {
        const collectionSet = options.isPrivateSale
          ? purchasingDetails.collectionSet
          : purchasingDetails.collectionSet.filter(
              (release) => !release.isSoldOut,
            );
        const collectionSetQuery = router.query.collectionSet;
        // if there's a pre-selected collection set in the url, select the products that are in the collection set
        if (collectionSetQuery && typeof collectionSetQuery === 'string') {
          const ids = collectionSetQuery.split(',');
          setCollectionSetProducts(
            collectionSet.map((item) => ({
              ...item,
              selected:
                item.selected ||
                ids.includes(shopifyGraphqlIdToAdminId(item.shopifyVariantId)),
            })),
          );
          setIsPreSelectedCollectionSet(true);
        } else {
          setCollectionSetProducts(collectionSet);
        }
      }

      if (purchasingDetails.collectoinSetDescription) {
        setCollectionSetDescription(purchasingDetails.collectoinSetDescription);
      }

      if (purchasingDetails.collectoinSetImage) {
        setCollectionSetImage(purchasingDetails.collectoinSetImage);
      }
    }
    setLoading(false);

    return void 0;
  }, [
    options.identifier,
    options.release,
    options.variantIdentifier,
    options.productsGateway,
    router.query.collectionSet,
    resetPurchasingState,
  ]);

  useEffect(() => {
    updatePurchaseInfo();
  }, [updatePurchaseInfo]);

  useOnBfCacheLoad(() => {
    setCheckoutLoading(false);
  });

  const getTaxInformationAttributes = async () => {
    const attributes: AttributeInput[] = [];
    const taxReponse =
      await options.aaProductsGateway.getTaxesForCountryCode(countryCode);

    attributes.push({ key: 'country_code', value: countryCode });
    if (taxReponse.tax && taxReponse.tax.taxType === GeneratedTaxType.Vat)
      attributes.push({
        key: 'vat_rate',
        value: taxReponse.tax.vatRate.toString(),
      });

    return attributes;
  };

  const getUserInformationAttributes = () => {
    const attributes: AttributeInput[] = [];
    if (isLoggedIn) {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_IN',
      });
    } else {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_OUT',
      });
    }
    return attributes;
  };

  const getCustomAttributes = async () => {
    const customAttributes: AttributeInput[] = [];

    // release gating
    if (options.releaseGating) {
      customAttributes.push(
        ...options.releaseGating.createCustomOrderAttributes(),
      );
    }

    // experimental features
    customAttributes.push({
      key: 'experimental_product_tile_subscription_button',
      value: experimentalProductTileSubscriptionButton ? 'true' : 'false',
    });
    customAttributes.push({
      key: 'experimental_framing_content_release_page',
      value: framingContentReleasePage ? 'true' : 'false',
    });
    customAttributes.push({
      key: 'experimental_framing_previewer_product_page',
      value: framingPreviewerProductPage ? 'true' : 'false',
    });
    customAttributes.push({
      key: 'experimental_framing_previewer_release_page',
      value: framingPreviewerReleasePage ? 'true' : 'false',
    });

    /**
     * Click events stop at the page — checkout redirects to Shopify, so the
     * order is the only place the arm and the revenue meet (PTE-4262). This
     * records the arm the visitor was bucketed into, not whether they used the
     * bar, which is what an intent to treat read of the test wants.
     */
    customAttributes.push({
      key: 'experimental_product_sticky_cta_bottom',
      value: experimentalStickyCtaBottom ? 'true' : 'false',
    });

    // private sales
    if (options.isPrivateSale && options.privateLinkIdentifier) {
      customAttributes.push({
        key: 'private_link_id',
        value: options.privateLinkIdentifier,
      });
    }

    // user information
    customAttributes.push(...getUserInformationAttributes());

    // tax information
    const taxAttributes = await getTaxInformationAttributes();
    customAttributes.push(...taxAttributes);

    const metaPixelFbc = getCookie('_fbc');
    const metaPixelFbcValue = metaPixelFbc?.split('.').at(-1);

    if (metaPixelFbcValue) {
      customAttributes.push({
        key: 'custom_fbc',
        value: metaPixelFbcValue,
      });
    }

    customAttributes.push({
      key: 'can_be_framed',
      value: options.productDetails?.frames.length ? 'true' : 'false',
    });

    return customAttributes;
  };

  const goToCheckout = async () => {
    if (product === null) return;
    setCheckoutLoading(true);

    const customAttributes = await getCustomAttributes();

    if (options.releaseGating?.requiresNftGating && isLoggedIn) {
      customAttributes.push(
        (
          await options.authGateway!.generateAvantPassToken({
            walletAddress,
            delegateCashVault: delegateCash.activeVaultAddress,
          })
        ).customAttribute,
      );
    }

    const collectionSetProductsFiltered = collectionSetProducts.filter(
      ({ selected }) => selected,
    );

    options.checkoutGateway
      .checkoutWithProduct({
        collectionSetProducts: collectionSetProductsFiltered,
        currency,
        customAttributes,
        frame,
        product,
        quantity,
        unlockCode,
      })
      .then((result) => {
        (async () => {
          let redirectURL = result;
          if (isLoggedIn && !options.isPrivateSale) {
            const multipassTokenResponse =
              await options.authGateway!.generateShopifyMultipassToken(result);
            if (!(multipassTokenResponse as AuthErrorResponse).error) {
              redirectURL = `${process.env.NEXT_PUBLIC_MULTIPASS_URL}${
                (multipassTokenResponse as GenerateMultipassTokenResponse).token
              }`;
            }
          }

          const response = await calculateOrderPricing({
            variables: {
              items,
              countryCode,
              currencyCode: DEFAULT_CURRENCY_CODE, // We're using default store currency for AML check because the limit is in default store currency
            },
          });
          const totals = response.data?.calculateOrderPricing;

          if (
            totals?.totalPrice &&
            normaliseShopifyPrice(
              totals.totalPrice,
              totals.currencyCode as CurrencyCode,
            ) > AML_LIMIT
          ) {
            setAmlCheckNeeded(true);
            const searchParams = new URLSearchParams({
              redirect: redirectURL,
            });
            router.push({
              pathname: '/aml',
              search: `?${searchParams.toString()}`,
            });
          } else {
            router.push(redirectURL);
          }
        })();
      });
  };

  const unlockProduct = async (code: string) => {
    if (product === null) return;

    setUnlockLoading(true);
    setUnlockCode(code);

    const state = await options.unlockGateway.unlockProduct(
      options.identifier,
      options.release,
      code,
    );

    const success = state.codeValid && !state.codeExpired;

    setUnlocked(success);
    setUnlockLoading(false);

    TrackUnlockCode({
      type: state.codeValid ? 'correct' : 'incorrect',
      productTitle: product.name,
      productPrice: product.price,
      productCurrencyCode: product.currency,
      code,
      expired: state.codeExpired,
    });

    if (success) setUnlockMessage('UNLOCK_SUCCESS');
    else if (!state.codeValid) setUnlockMessage('UNLOCK_FAILED');
    else setUnlockMessage('UNLOCK_EXPIRED');
  };

  const setQuantity = (_quantity: number) => {
    setProductQuantity(_quantity);

    if (frame) {
      frame.variant.consolidatedVariants[0].quantity = _quantity;
    }
  };

  const dispatchDate = useMemo(
    () =>
      getDispatchDate({
        framed: framedByDefault(product?.framingOffered) || Boolean(frame),
        framedDispatchDate: product?.framedDispatchDate || null,
        unframedDispatchDate: product?.unframedDispatchDate || null,
        launchDate: product?.launchDate || null,
      }),
    [
      frame,
      product?.framedDispatchDate,
      product?.framingOffered,
      product?.launchDate,
      product?.unframedDispatchDate,
    ],
  );

  const collectionSetUpdateQuantity = (id: string, newQuantity: number) => {
    const newState = collectionSetProducts.map((r) =>
      r.shopifyId === id
        ? {
            ...r,
            quantity: newQuantity,
          }
        : r,
    );

    setCollectionSetProducts(newState);

    if (frame) {
      const frameConsolidatedVariantIndex =
        frame.variant.consolidatedVariants.findIndex(
          ({ shopifyProductId }) => shopifyProductId === id,
        );

      if (frameConsolidatedVariantIndex !== -1) {
        frame.variant.consolidatedVariants[
          frameConsolidatedVariantIndex
        ].quantity = newQuantity;
      }
    }
  };

  const collectionSetToggleId = (id: string) => {
    setFrame(null);

    const newState = collectionSetProducts.map((r) =>
      r.shopifyId === id
        ? {
            ...r,
            selected: !r.selected,
          }
        : r,
    );

    setCollectionSetProducts(newState);
  };

  const collectionSetToggleAll = () => {
    setBeforeCollectionSetToggleAll(
      (before) => before ?? { products: collectionSetProducts, frame },
    );
    setFrame(null);

    const newState = collectionSetProducts.map((rr) => ({
      ...rr,
      selected: true,
    }));

    setCollectionSetProducts(newState);
  };

  // Back to how the page was before `collectionSetToggleAll`, e.g. when the
  // collection set drawer is closed.
  const collectionSetRestore = () => {
    const before = beforeCollectionSetToggleAll;
    if (!before) return;

    setBeforeCollectionSetToggleAll(null);
    restoredFrame.current = { frame: before.frame };
    setCollectionSetProducts(before.products);
  };

  return {
    loading,
    checkoutLoading,
    dispatchDate,
    product,
    frame,
    frameOptions: frameOptionsConsolidated,
    productFrameOptions,
    // The page's own frame, kept while the whole collection set is selected.
    productFrame: beforeCollectionSetToggleAll
      ? beforeCollectionSetToggleAll.frame
      : frame,
    isFrameFromQuery: linkedFrame !== undefined && frame === linkedFrame,
    unlocked,
    unlockLoading,
    unlockMessage,
    unlockCode,
    requiresUnlock,
    quantity,
    releaseGating: options.releaseGating,
    requiresGating,
    canPurchase,
    release: options.release,
    releaseType: options.releaseType,
    setFrame,
    goToCheckout,
    unlockProduct,
    setUnlockCode,
    setQuantity,
    collectionSet: collectionSetProducts.length ? collectionSetProducts : null,
    isPreSelectedCollectionSet,
    setIsPreSelectedCollectionSet,
    collectionSetDescription,
    collectionSetImage,
    collectionSetToggleId,
    collectionSetToggleAll,
    collectionSetRestore,
    collectionSetUpdateQuantity,
    isFramedByDefault: isFramedByDefaultFeatureEnabled,
    amlCheckNeeded,
  };
}
